import React from 'react';
import { Squirrel } from 'lucide-react';
import { explainerSlugKey } from 'src/shared/constants/portfolio';
import { PortfolioData } from 'src/app/portfolio/state/api/portfolioQuery';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { SortOrder } from 'src/shared/types';
import { HabitatInsightsMetricType } from 'src/shared/types/graph/survey';
import PortfolioExploreButton from './PortfolioExploreButton';
import PortfolioGaugeChart from './PortfolioGaugeChart';
import SummaryCard from './SummaryCard';

const BiodiversityIntactnessChart = ({ portfolioData }: { portfolioData: PortfolioData }) => {
    const biodiversityIntactness = portfolioData.summary.metrics.find(
        metric => metric.name === HabitatInsightsMetricType.BIODIVERSITY_INTACTNESS_INDEX
    );
    const navigate = useAppNavigation();
    const { currentCustomerId } = useCustomer();

    if (!biodiversityIntactness || !biodiversityIntactness.data || !biodiversityIntactness.year) {
        return null;
    }

    let intactnessCategory = 'Severely degraded';
    let intactnessColor = 'red';
    if (biodiversityIntactness.data > 0.3 && biodiversityIntactness.data <= 0.6) {
        intactnessCategory = 'Degraded';
    } else if (biodiversityIntactness.data > 0.6 && biodiversityIntactness.data <= 0.9) {
        intactnessCategory = 'Moderately intact';
        intactnessColor = 'orange';
    } else if (biodiversityIntactness.data > 0.9) {
        intactnessCategory = 'Intact';
        intactnessColor = 'green';
    }

    const chartData = {
        chartValue: biodiversityIntactness.data,
        startLabel: (
            <>
                Severley
                <br />
                degraded
            </>
        ),
        endLabel: 'Intact',
    };

    const biiSummary = portfolioData.summary.biiSiteSummary;

    // Find the "next worst" category that has sites
    let worstCategorySites = 0;
    let worstCategoryLabel = '';

    if (biiSummary.severely_degraded > 0) {
        worstCategorySites = biiSummary.severely_degraded;
        worstCategoryLabel = 'severely degraded';
    } else if (biiSummary.low_intactness > 0) {
        worstCategorySites = biiSummary.low_intactness;
        worstCategoryLabel = 'low intactness';
    } else if (biiSummary.moderate_intactness > 0) {
        worstCategorySites = biiSummary.moderate_intactness;
        worstCategoryLabel = 'moderate intactness';
    } else {
        worstCategorySites = biiSummary.intact;
        worstCategoryLabel = 'intact';
    }

    const handleExploreClick = () => {
        navigate.toPortfolioSiteExplorer({
            customerId: currentCustomerId,
            selectedMetric: HabitatInsightsMetricType.BIODIVERSITY_INTACTNESS_INDEX,
            sortDirection: SortOrder.ASC,
            view: 'list',
        });
    };

    return (
        <SummaryCard
            dataTestId='portfolio-biodiversity-intactness-chart'
            containerClasses='gap-3 p-5'
            cardIcon={<Squirrel className='text-primary' />}
            cardTitle={
                <span className='flex flex-col items-start'>
                    Biodiversity Intactness Index
                    <span className='text-sm text-grey-80'>For {biodiversityIntactness.year}</span>
                </span>
            }
            cardSubtitle='Measures the remaining biodiversity relative to an undisturbed baseline.'
            titleExplainerSlug={{
                defaultSlug: explainerSlugKey.portfolioBiodiversityIntactnessIndex,
                explainerSlug: `${explainerSlugKey.portfolioBiodiversityIntactnessIndex}-te`,
            }}
            cardBody={
                <PortfolioGaugeChart
                    sites={worstCategorySites}
                    totalSites={portfolioData.summary.numberSites}
                    chartData={chartData}
                    chartColor={intactnessColor}
                    category={intactnessCategory}
                    summaryText={
                        <div className='mb-2'>
                            <span className='text-sm text-grey-80'>of your sites {worstCategoryLabel === 'intact' ? 'are' : 'have'} </span>
                            <span className='text-sm text-grey-80 font-bold'>{worstCategoryLabel}</span>
                        </div>
                    }
                />
            }
            action={<PortfolioExploreButton sectionName='biodiversity-intactness' onClick={handleExploreClick} />}
            frameworks={biodiversityIntactness.frameworks}
        />
    );
};

export default BiodiversityIntactnessChart;
